import API from '@/api/api-admin';
import API_V3 from '@/api/api-admin-v3';
import listRoutes from '@/api/list-routes';
import { adaptHydraPagination } from '@/utils/hydra-pagination';

async function fetchStoresForClient(clientId) {
  const path = listRoutes.stores.replace(':id', String(clientId));
  const res = await API.get(`${path}?limit=500`, { skipGlobalErrorHandler: true });
  return res.data?.items || [];
}

async function fetchStoreIdsForClient(clientId) {
  const stores = await fetchStoresForClient(clientId);
  return stores.map((store) => store.id);
}

function filterSellersByStoreIds(sellers, storeIds) {
  if (!storeIds.length) {
    return [];
  }
  const storeIdSet = new Set(storeIds);
  return sellers.filter((seller) =>
    (seller.stores || []).some((store) => storeIdSet.has(store.id))
  );
}

function parseCollection(data, { page = 1, itemsPerPage = 10 } = {}) {
  const items = (data['hydra:member'] || []).map((seller) => ({
    ...seller,
    storesLabel: (seller.stores || []).map((s) => s.label).filter(Boolean).join(', '),
    tradeInsCount: seller.tradeInsCount ?? 0,
  }));
  return {
    items,
    pagination: adaptHydraPagination(data, { page, itemsPerPage }),
  };
}

const state = {
  sellers: null,
  seller: null,
  sellerLoading: false,
  sellerLoadError: null,
  clientStores: [],
};

const mutations = {
  SET_SELLERS(state, value) {
    state.sellers = value;
  },
  SET_SELLER(state, seller) {
    state.seller = seller;
  },
  SET_SELLER_LOADING(state, value) {
    state.sellerLoading = value;
  },
  SET_SELLER_LOAD_ERROR(state, value) {
    state.sellerLoadError = value;
  },
  SET_CLIENT_STORES(state, stores) {
    state.clientStores = stores;
  },
};

const actions = {
  fetchSellers({ commit }, payload) {
    const clientId = payload && typeof payload === 'object' ? payload.clientId : payload;
    const page = Number(payload?.page) > 0 ? Number(payload.page) : 1;
    const itemsPerPage = Number(payload?.itemsPerPage) > 0 ? Number(payload.itemsPerPage) : 10;

    commit('SET_SELLER_LOADING', true);
    return API_V3.get('store-sellers', { params: { page, itemsPerPage } })
      .then((res) => parseCollection(res.data, { page, itemsPerPage }))
      .then(async (table) => {
        if (clientId) {
          const storeIds = await fetchStoreIdsForClient(clientId);
          table.items = filterSellersByStoreIds(table.items, storeIds);
        }
        commit('SET_SELLERS', table);
        return table;
      })
      .finally(() => commit('SET_SELLER_LOADING', false));
  },
  fetchSeller({ commit }, id) {
    commit('SET_SELLER_LOADING', true);
    commit('SET_SELLER', null);
    commit('SET_SELLER_LOAD_ERROR', null);
    return API_V3.get(`store-sellers/${id}`)
      .then((res) => {
        commit('SET_SELLER', res.data);
        return res.data;
      })
      .catch((error) => {
        commit('SET_SELLER_LOAD_ERROR', error?.response?.status ?? null);
        throw error;
      })
      .finally(() => commit('SET_SELLER_LOADING', false));
  },
  fetchClientStores({ commit }, clientId) {
    if (!clientId) {
      commit('SET_CLIENT_STORES', []);
      return Promise.resolve([]);
    }

    return fetchStoresForClient(clientId)
      .then((stores) => {
        commit('SET_CLIENT_STORES', stores);
        return stores;
      })
      .catch(() => {
        commit('SET_CLIENT_STORES', []);
        return [];
      });
  },
  createSeller({ commit }, data) {
    commit('SET_SELLER_LOADING', true);
    return API_V3.post('store-sellers', data, { skipGlobalErrorHandler: true })
      .then((res) => {
        commit('SET_SELLER', res.data);
        return res.data;
      })
      .finally(() => commit('SET_SELLER_LOADING', false));
  },
  resetSellerPassword(_, { id, password }) {
    return API_V3.put(`store-sellers/${id}/password`, { password }).then((res) => res.data);
  },
  deleteSeller({ commit }, id) {
    commit('SET_SELLER_LOADING', true);
    return API_V3.delete(`store-sellers/${id}`)
      .then(() => commit('SET_SELLER', null))
      .finally(() => commit('SET_SELLER_LOADING', false));
  },
};

const storeSellers = { state, mutations, actions, getters: {} };

export default storeSellers;
